import { useState, useEffect, useCallback } from 'react';
import { useConnector } from '../contexts/ConnectorContext';
import { ProjectType, ProjectStatus } from '../types/timesheet.types';
import { DatePickerInput } from './DatePickerInput';

interface ProjItem {
  id: string;
  projectCode: string;
  projectName: string;
  type: string;
  costCentre: string;
  projectOwnerName: string;
  projectOwnerEmail: string;
  assignedPMName: string;
  assignedPMEmail: string;
  startDate: string;
  endDate: string;
  estimatedHours: number | null;
  assignedTeamEmails: string[];
  assignedTeamNames: string[];
  status: string;
  description: string;
}

interface PersonOption {
  id: string;
  name: string;
  email: string;
}

interface ProjForm {
  projectCode: string;
  projectName: string;
  type: string;
  costCentre: string;
  /** Internal UI: person ID of selected project owner */
  projectOwnerPersonId: string;
  /** Internal UI: person ID of selected PM */
  assignedPMPersonId: string;
  startDate: string;
  endDate: string;
  estimatedHours: string;
  /** Internal UI: person IDs of assigned team members */
  assignedTeamPersonIds: string[];
  description: string;
  status: string;
}

type FilterType = string;

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
function formatDate(iso: string | null | undefined): string {
  if (!iso || iso === '1970-01-01') return '—';
  const [y, m, d] = iso.split('-');
  return `${d} ${MONTHS[parseInt(m, 10) - 1]} ${y}`;
}

const TYPE_BADGE: Record<string, string> = {
  [ProjectType.RD]: 'badge-rd',
  [ProjectType.GA]: 'badge-ga',
  [ProjectType.Leave]: 'badge-leave',
  [ProjectType.Commercial]: 'badge-commercial',
  [ProjectType.Standard]: 'badge-standard',
};

const FILTER_LABELS: [FilterType, string][] = [
  ['all', 'All'],
  [ProjectType.RD, 'R&D'],
  [ProjectType.GA, 'G&A'],
  [ProjectType.Leave, 'Leave'],
  [ProjectType.Commercial, 'Commercial'],
  [ProjectType.Standard, 'Standard'],
];

const EMPTY_FORM: ProjForm = {
  projectCode: '',
  projectName: '',
  type: '',
  costCentre: '',
  projectOwnerPersonId: '',
  assignedPMPersonId: '',
  startDate: '',
  endDate: '',
  estimatedHours: '',
  assignedTeamPersonIds: [],
  description: '',
  status: ProjectStatus.Active,
};

function initials(name: string) {
  return name.split(' ').map(s => s[0] || '').slice(0, 2).join('').toUpperCase();
}

function TeamPills({ names, max = 2 }: { names: string[]; max?: number }) {
  if (!names.length) return <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>—</span>;
  const shown = names.slice(0, max);
  const extra = names.length - shown.length;
  return (
    <div className="team-stack" title={names.join(', ')}>
      {shown.map(n => (
        <span key={n} className="team-pill">
          <span className="av-xs">{initials(n)}</span>
          {n.split(' ')[0]}
        </span>
      ))}
      {extra > 0 && <span className="team-count">+{extra}</span>}
    </div>
  );
}

export function ProjectsAdmin() {
  const connector = useConnector();

  const [items, setItems] = useState<ProjItem[]>([]);
  const [people, setPeople] = useState<PersonOption[]>([]);
  const [costCentres, setCostCentres] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [filter, setFilter] = useState<FilterType>('all');
  const [search, setSearch] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<ProjForm>(EMPTY_FORM);
  const [formError, setFormError] = useState(false);

  const [archiveTarget, setArchiveTarget] = useState<ProjItem | null>(null);
  const [archiving, setArchiving] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);

  const [teamSearch, setTeamSearch] = useState('');
  const [teamDropdownOpen, setTeamDropdownOpen] = useState(false);

  const emailToId: Record<string, string> = {};
  for (const p of people) emailToId[p.email] = p.id;

  const loadData = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [projects, persons, centres] = await Promise.all([
        connector.getAllProjects(),
        connector.getPeople(),
        connector.getCostCentreOptions(),
      ]);
      setItems(projects.filter(p => !p.id.startsWith('virtual-')).map(p => ({
        id: p.id,
        projectCode: p.projectCode,
        projectName: p.projectName,
        type: p.type,
        costCentre: p.costCentre,
        projectOwnerName: p.projectOwnerName,
        projectOwnerEmail: p.projectOwnerEmail,
        assignedPMName: p.assignedPMName,
        assignedPMEmail: p.assignedPMEmail,
        startDate: p.startDate,
        endDate: p.endDate ?? '',
        estimatedHours: p.estimatedHours,
        assignedTeamEmails: [...p.assignedTeamEmails],
        assignedTeamNames: [...p.assignedTeamNames],
        status: p.status,
        description: p.description,
      })));
      setPeople(persons.map(p => ({ id: p.id, name: p.fullName, email: p.m365Email })).sort((a, b) => a.name.localeCompare(b.name)));
      setCostCentres(centres);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [connector]);

  useEffect(() => { void loadData(); }, [loadData]);

  const filtered = items.filter(p =>
    (filter === 'all' || p.type === filter) &&
    (!search ||
      p.projectCode.toLowerCase().includes(search.toLowerCase()) ||
      p.projectName.toLowerCase().includes(search.toLowerCase()) ||
      p.description.toLowerCase().includes(search.toLowerCase()))
  ).sort((a, b) => a.projectCode.localeCompare(b.projectCode));

  function openAdd() {
    setEditId(null);
    setForm({ ...EMPTY_FORM });
    setFormError(false);
    setSaveError(null);
    setTeamSearch('');
    setModalOpen(true);
  }

  function openEdit(id: string) {
    const p = items.find(x => x.id === id);
    if (!p) return;
    setEditId(id);
    setForm({
      projectCode: p.projectCode,
      projectName: p.projectName,
      type: p.type,
      costCentre: p.costCentre,
      projectOwnerPersonId: emailToId[p.projectOwnerEmail] ?? '',
      assignedPMPersonId: emailToId[p.assignedPMEmail] ?? '',
      startDate: p.startDate,
      endDate: p.endDate,
      estimatedHours: p.estimatedHours != null ? String(p.estimatedHours) : '',
      assignedTeamPersonIds: p.assignedTeamEmails.map(email => emailToId[email] ?? '').filter(Boolean),
      description: p.description,
      status: p.status,
    });
    setFormError(false);
    setSaveError(null);
    setTeamSearch('');
    setModalOpen(true);
  }

  async function save() {
    if (!form.projectCode.trim()) { setFormError(true); return; }
    setSaving(true);
    setSaveError(null);
    try {
      const owner = people.find(p => p.id === form.projectOwnerPersonId);
      const pm = people.find(p => p.id === form.assignedPMPersonId);
      const teamMembers = form.assignedTeamPersonIds.map(id => people.find(p => p.id === id)).filter(Boolean) as PersonOption[];
      const data = {
        projectCode: form.projectCode.trim(),
        projectName: form.projectName.trim() || form.projectCode.trim(),
        type: form.type,
        costCentre: form.costCentre,
        projectOwnerName: owner?.name ?? '',
        projectOwnerEmail: owner?.email ?? '',
        assignedPMName: pm?.name ?? '',
        assignedPMEmail: pm?.email ?? '',
        startDate: form.startDate || null,
        endDate: form.endDate || null,
        estimatedHours: parseInt(form.estimatedHours) || null,
        assignedTeamNames: teamMembers.map(p => p.name).join(','),
        assignedTeamEmails: teamMembers.map(p => p.email).join(','),
        isResearchDevelopment: (form.type as ProjectType) === ProjectType.RD,
        status: form.status,
        description: form.description.trim(),
      };
      if (editId) {
        await connector.updateProject(editId, data);
        setItems(prev => prev.map(p => p.id === editId ? {
          ...p,
          projectCode: data.projectCode,
          projectName: data.projectName,
          type: data.type,
          costCentre: data.costCentre,
          projectOwnerName: data.projectOwnerName,
          projectOwnerEmail: data.projectOwnerEmail,
          assignedPMName: data.assignedPMName,
          assignedPMEmail: data.assignedPMEmail,
          startDate: data.startDate ?? '',
          endDate: data.endDate ?? '',
          estimatedHours: data.estimatedHours,
          assignedTeamEmails: data.assignedTeamEmails.split(',').filter(Boolean),
          assignedTeamNames: data.assignedTeamNames.split(',').filter(Boolean),
          status: data.status,
          description: data.description,
        } : p));
      } else {
        const created = await connector.createProject(data);
        setItems(prev => [{
          id: created.id,
          projectCode: created.projectCode,
          projectName: created.projectName,
          type: created.type,
          costCentre: created.costCentre,
          projectOwnerName: created.projectOwnerName,
          projectOwnerEmail: created.projectOwnerEmail,
          assignedPMName: created.assignedPMName,
          assignedPMEmail: created.assignedPMEmail,
          startDate: created.startDate,
          endDate: created.endDate ?? '',
          estimatedHours: created.estimatedHours,
          assignedTeamEmails: [...created.assignedTeamEmails],
          assignedTeamNames: [...created.assignedTeamNames],
          status: created.status,
          description: created.description,
        }, ...prev]);
      }
      setModalOpen(false);
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function toggleStatus(id: string) {
    const p = items.find(x => x.id === id);
    if (!p) return;
    const newStatus = (p.status as ProjectStatus) === ProjectStatus.Active ? ProjectStatus.Closed : ProjectStatus.Active;
    try {
      await connector.updateProject(id, { status: newStatus });
      setItems(prev => prev.map(x => x.id === id ? { ...x, status: newStatus } : x));
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Update failed');
    }
  }

  function requestArchive(id: string) {
    const p = items.find(x => x.id === id);
    if (!p) return;
    setArchiveError(null);
    setArchiveTarget(p);
  }

  async function confirmArchive() {
    if (!archiveTarget) return;
    setArchiving(true);
    setArchiveError(null);
    try {
      await connector.updateProject(archiveTarget.id, { status: ProjectStatus.Archived });
      setItems(prev => prev.map(p => p.id === archiveTarget.id ? { ...p, status: ProjectStatus.Archived } : p));
      setArchiveTarget(null);
    } catch (e) {
      setArchiveError(e instanceof Error ? e.message : 'Archive failed');
    } finally {
      setArchiving(false);
    }
  }

  function addTeamMember(personId: string) {
    if (personId && !form.assignedTeamPersonIds.includes(personId)) {
      setForm(f => ({ ...f, assignedTeamPersonIds: [...f.assignedTeamPersonIds, personId] }));
    }
  }

  function removeTeamMember(personId: string) {
    setForm(f => ({ ...f, assignedTeamPersonIds: f.assignedTeamPersonIds.filter(id => id !== personId) }));
  }

  const availablePeople = people.filter(p => !form.assignedTeamPersonIds.includes(p.id));

  if (loading) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>
        Loading projects…
      </div>
    );
  }

  if (loadError) {
    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
        <span style={{ color: 'var(--danger)', fontSize: 14 }}>{loadError}</span>
        <button className="btn btn-ghost" onClick={() => void loadData()}>Retry</button>
      </div>
    );
  }

  return (
    <>
      <div className="ts-topbar">
        <div className="topbar-l">
          <div className="crumb">
            <span>Admin</span><span className="sep">/</span><span className="cur">Projects &amp; Tasks</span>
          </div>
        </div>
        <div className="topbar-r">
          <button className="btn btn-ghost" onClick={openAdd}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ width: 13, height: 13, strokeWidth: 2 }}><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
            Add Project
          </button>
        </div>
      </div>

      <div className="proj-content">
        <div className="proj-filter-bar">
          <div className="search-wrap">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
            <input placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <div className="proj-filter-tabs">
            {FILTER_LABELS.map(([f, label]) => (
              <button key={f} className={`filter-tab${filter === f ? ' active' : ''}`} onClick={() => setFilter(f)}>
                {label}
              </button>
            ))}
          </div>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{filtered.length} items</span>
        </div>

        <div className="tbl-scroll-wrap">
        <div className="admin-hdr proj-grid">
          <div>Project / Task</div>
          <div style={{ textAlign: 'center' }}>Type</div>
          <div style={{ textAlign: 'center' }}>Cost Centre</div>
          <div>Project Owner</div>
          <div>Assigned Team</div>
          <div style={{ textAlign: 'center' }}>Start Date</div>
          <div style={{ textAlign: 'center' }}>End Date</div>
          <div style={{ textAlign: 'center' }}>Est. Hrs</div>
          <div style={{ textAlign: 'center' }}>Status</div>
          <div style={{ textAlign: 'right' }}>Actions</div>
        </div>

        {filtered.map(p => {
          const ownerName = p.projectOwnerName || p.projectOwnerEmail || '—';
          const badge = TYPE_BADGE[p.type] ?? 'badge-standard';
          const isActive = (p.status as ProjectStatus) === ProjectStatus.Active;
          const isArchived = (p.status as ProjectStatus) === ProjectStatus.Archived;
          return (
            <div key={p.id} className={`admin-row proj-grid${!isActive ? ' inactive' : ''}`}>
              <div>
                <div className="task-name">
                  {p.projectCode}
                  <span className={`badge ${badge}`}>{p.type}</span>
                </div>
                {p.projectName && p.projectName !== p.projectCode && (
                  <div className="task-meta">{p.projectName}</div>
                )}
                {p.description && <div className="task-meta">{p.description}</div>}
              </div>
              <div style={{ textAlign: 'center' }}>
                <span className={`badge ${badge}`}>{p.type}</span>
              </div>
              <div style={{ textAlign: 'center', fontSize: 12.5, color: 'var(--text-muted)' }}>{p.costCentre}</div>
              <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{ownerName}</div>
              <div><TeamPills names={p.assignedTeamNames} max={2} /></div>
              <div style={{ textAlign: 'center', fontSize: 12, color: 'var(--text-muted)' }}>{formatDate(p.startDate)}</div>
              <div style={{ textAlign: 'center', fontSize: 12, color: 'var(--text-muted)' }}>{formatDate(p.endDate)}</div>
              <div style={{ textAlign: 'center', fontSize: 12.5, fontFamily: "'DM Mono',monospace" }}>
                {p.estimatedHours ? `${p.estimatedHours}h` : '—'}
              </div>
              <div style={{ textAlign: 'center' }}>
                {isArchived
                  ? <span className="toggle-btn off" style={{ cursor: 'default', opacity: 0.7 }}>Archived</span>
                  : <button className={`toggle-btn ${isActive ? 'on' : 'off'}`} onClick={() => void toggleStatus(p.id)}>
                      {isActive ? 'Active' : 'Closed'}
                    </button>
                }
              </div>
              <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                <button className="icon-btn" onClick={() => openEdit(p.id)} title="Edit">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M11 4H4a2 2 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                </button>
                {!isArchived && (
                  <button className="icon-btn" onClick={() => requestArchive(p.id)} title="Archive" style={{ color: '#b45309' }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><polyline points="21 8 21 21 3 21 3 8" /><rect x="1" y="3" width="22" height="5" /><line x1="10" y1="12" x2="14" y2="12" /></svg>
                  </button>
                )}
              </div>
            </div>
          );
        })}
        </div>{/* tbl-scroll-wrap */}
      </div>

      {/* Project Modal */}
      <div className={`adm-modal-bg${modalOpen ? ' open' : ''}`}>
        <div className="adm-modal">
          <div className="adm-modal-hdr">
            <span className="adm-modal-hdr-title">{editId ? 'Edit Project' : 'Add Project'}</span>
            <button className="adm-modal-x" onClick={() => setModalOpen(false)}>×</button>
          </div>
          <div className="adm-modal-body">
            <div className="form-row cols2">
              <div className="form-field">
                <label>Project Code <span className="req">*</span></label>
                <input
                  className={`finput${formError && !form.projectCode.trim() ? ' error' : ''}`}
                  value={form.projectCode}
                  onChange={e => setForm(f => ({ ...f, projectCode: e.target.value }))}
                  placeholder="e.g. EVV-CORE-001"
                />
              </div>
              <div className="form-field">
                <label>Project Name</label>
                <input
                  className="finput"
                  value={form.projectName}
                  onChange={e => setForm(f => ({ ...f, projectName: e.target.value }))}
                  placeholder="e.g. Core Platform Development"
                />
              </div>
            </div>
            <div className="form-row cols2">
              <div className="form-field">
                <label>Type <span className="req">*</span></label>
                <select className="finput" value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
                  <option value="">— Select Type —</option>
                  {Object.values(ProjectType).map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="form-field">
                <label>Cost Centre</label>
                <select className="finput" value={form.costCentre} onChange={e => setForm(f => ({ ...f, costCentre: e.target.value }))}>
                  <option value="">— Select Cost Centre —</option>
                  {costCentres.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div className="form-row cols2">
              <div className="form-field">
                <label>Project Owner</label>
                <select className="finput" value={form.projectOwnerPersonId} onChange={e => setForm(f => ({ ...f, projectOwnerPersonId: e.target.value }))}>
                  <option value="">— Select person —</option>
                  {people.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div className="form-field">
                <label>Assigned PM</label>
                <select className="finput" value={form.assignedPMPersonId} onChange={e => setForm(f => ({ ...f, assignedPMPersonId: e.target.value }))}>
                  <option value="">— Select person —</option>
                  {people.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
            </div>
            <div className="form-row cols2">
              <div className="form-field">
                <label>Start Date</label>
                <DatePickerInput value={form.startDate} onChange={v => setForm(f => ({ ...f, startDate: v }))} />
              </div>
              <div className="form-field">
                <label>End Date</label>
                <DatePickerInput value={form.endDate} onChange={v => setForm(f => ({ ...f, endDate: v }))} />
              </div>
            </div>
            <div className="form-row cols2">
              <div className="form-field">
                <label>Est. Hours</label>
                <input className="finput" type="number" min="0" value={form.estimatedHours} onChange={e => setForm(f => ({ ...f, estimatedHours: e.target.value }))} placeholder="e.g. 200" />
              </div>
              <div className="form-field">
                <label>Status</label>
                <select className="finput" value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                  {Object.values(ProjectStatus).filter(s => s !== ProjectStatus.Archived).map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div className="form-field">
              <label>Assigned Team <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>— only these people see this project in their dropdown</span></label>
              <div className="chips-input">
                {form.assignedTeamPersonIds.length === 0
                  ? <span style={{ fontSize: 12, color: 'var(--text-muted)', padding: '6px 4px' }}>No one assigned yet — add team members below.</span>
                  : form.assignedTeamPersonIds.map(id => {
                    const person = people.find(p => p.id === id);
                    const name = person?.name ?? id;
                    return (
                      <span key={id} className="team-pill">
                        <span className="av-xs">{initials(name)}</span>
                        {name}
                        <span className="rm" onClick={() => removeTeamMember(id)}>×</span>
                      </span>
                    );
                  })
                }
              </div>
              <div style={{ position: 'relative', marginTop: 6 }}>
                <input
                  className="finput"
                  type="text"
                  placeholder="Search name…"
                  value={teamSearch}
                  autoComplete="off"
                  onChange={e => { setTeamSearch(e.target.value); setTeamDropdownOpen(true); }}
                  onFocus={() => setTeamDropdownOpen(true)}
                  onBlur={() => setTimeout(() => setTeamDropdownOpen(false), 150)}
                  style={{ width: '100%' }}
                />
                {teamDropdownOpen && (
                  <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 9999,
                    background: 'var(--surface)', border: '1px solid var(--border)',
                    borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
                    maxHeight: 220, overflowY: 'auto', marginTop: 2,
                  }}>
                    {availablePeople.filter(p => !teamSearch || p.name.toLowerCase().includes(teamSearch.toLowerCase())).length === 0
                      ? <div style={{ padding: '10px 14px', color: 'var(--text-muted)', fontSize: 13 }}>No matches</div>
                      : availablePeople
                          .filter(p => !teamSearch || p.name.toLowerCase().includes(teamSearch.toLowerCase()))
                          .map(p => (
                            <div
                              key={p.id}
                              onMouseDown={() => { addTeamMember(p.id); setTeamSearch(''); setTeamDropdownOpen(false); }}
                              style={{ padding: '9px 14px', cursor: 'pointer', fontSize: 14 }}
                              onMouseEnter={e => (e.currentTarget.style.background = 'var(--surface-hover, rgba(255,255,255,0.06))')}
                              onMouseLeave={e => (e.currentTarget.style.background = '')}
                            >
                              {p.name}
                            </div>
                          ))
                    }
                  </div>
                )}
              </div>
            </div>
            <div className="form-field">
              <label>Description</label>
              <textarea className="fnote" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Optional..." />
            </div>
            <div className={`ferr${formError ? ' show' : ''}`}>Project Code is required.</div>
            {saveError && <div className="ferr show">{saveError}</div>}
            <div className="adm-modal-footer">
              <button className="btn btn-ghost" onClick={() => setModalOpen(false)} disabled={saving}>Cancel</button>
              <button className="btn btn-primary" onClick={() => void save()} disabled={saving}>{saving ? 'Saving…' : 'Save Item'}</button>
            </div>
          </div>
        </div>
      </div>

      {/* Archive Confirmation Modal */}
      {archiveTarget && (
        <div
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(10,12,20,0.6)',
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)',
            zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 24,
            animation: 'fadeIn 0.15s ease',
          }}
        >
          <div
            style={{
              background: 'var(--surface)',
              borderRadius: 16,
              width: '100%', maxWidth: 440,
              boxShadow: '0 24px 64px rgba(0,0,0,0.28), 0 0 0 1px rgba(0,0,0,0.06)',
              overflow: 'hidden',
              animation: 'slideUp 0.2s cubic-bezier(0.16,1,0.3,1)',
            }}
          >
            <div style={{ height: 4, background: 'linear-gradient(90deg, #b45309, #d97706)' }} />

            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '20px 20px 0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                  width: 40, height: 40, borderRadius: '50%',
                  background: 'rgba(180,83,9,0.10)',
                  border: '1.5px solid rgba(180,83,9,0.22)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="#b45309" style={{ width: 18, height: 18, strokeWidth: 2.5 }}>
                    <polyline points="21 8 21 21 3 21 3 8" />
                    <rect x="1" y="3" width="22" height="5" />
                    <line x1="10" y1="12" x2="14" y2="12" />
                  </svg>
                </div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', letterSpacing: '-0.01em' }}>
                    Archive Project
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 1 }}>
                    Project will be hidden from all timesheet dropdowns
                  </div>
                </div>
              </div>
              <button
                onClick={() => { if (!archiving) setArchiveTarget(null); }}
                style={{
                  background: 'none', border: 'none', cursor: archiving ? 'not-allowed' : 'pointer',
                  color: 'var(--text-dim)', fontSize: 18, lineHeight: 1,
                  padding: '2px 4px', borderRadius: 4,
                  opacity: archiving ? 0.5 : 1,
                }}
              >
                ✕
              </button>
            </div>

            <div style={{ padding: '16px 20px 0' }}>
              <div style={{
                background: 'var(--surface2)',
                border: '1px solid var(--border)',
                borderRadius: 10, padding: '12px 14px',
                marginBottom: 14,
              }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
                  {archiveTarget.projectCode}
                </div>
                {archiveTarget.projectName && archiveTarget.projectName !== archiveTarget.projectCode && (
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                    {archiveTarget.projectName}
                  </div>
                )}
              </div>
              <div style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.55 }}>
                Archiving this project will set its status to Archived. It will no longer appear in the project dropdown when submitting timesheets.
              </div>
              {archiveError && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  fontSize: 12, color: 'var(--red)', marginTop: 10,
                }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" style={{ width: 12, height: 12, strokeWidth: 2.5, flexShrink: 0 }}>
                    <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><circle cx="12" cy="16" r="0.5" fill="currentColor" />
                  </svg>
                  {archiveError}
                </div>
              )}
            </div>

            <div style={{
              display: 'flex', gap: 8, justifyContent: 'flex-end',
              padding: '16px 20px 20px',
              borderTop: '1px solid var(--border)',
              marginTop: 16,
            }}>
              <button className="btn btn-ghost" onClick={() => setArchiveTarget(null)} disabled={archiving}>
                Cancel
              </button>
              <button
                className="btn"
                onClick={() => void confirmArchive()}
                disabled={archiving}
                style={{ minWidth: 120, background: '#b45309', color: '#fff', border: 'none' }}
              >
                {archiving ? (
                  <><span className="spinner" style={{ width: 12, height: 12, borderWidth: 2 }} />&nbsp;Archiving…</>
                ) : (
                  'Archive Project'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
